import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  PayrollPlusesJustificationDialogComponent,
  PayrollPlusesJustificationDialogData,
} from '../../../../shared/payroll-pluses-justification-dialog/payroll-pluses-justification-dialog.component';
import { Payroll } from 'src/app/models/payroll.model';
import { PayrollPlusJustification } from 'src/app/models/payroll-plus-justification.model';
import {
  PAYROLL_CONCEPT_KIND,
  PAYROLL_CONCEPT_SLUG,
  bisualPlusPayrollLines,
  bisualPlusesTotal,
  extraPayrollConceptLineChips,
  payrollLineAmountBySlug,
  payrollLineMetaBoolean,
  payrollLineMetaNumber,
} from 'src/app/models/payroll-line.utils';
import { PayrollConceptKind } from 'src/app/models/payroll-concept.model';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services';
import { UserService } from 'src/app/services/user.service';

dayjs.extend(utc);

interface PayrollAdjustmentChip {
  key: string;
  emoji: string;
  label: string;
  amount: number | null;
  text?: string;
  kind?: PayrollConceptKind;
  is_expected_normal?: boolean;
  is_kpi_cut?: boolean;
}

const PER_HOUR_CONTRACT_SLUGS = ['per-hours', 'practice'];
const SALARY_COLUMNS = [
  'period',
  'brute_base_salary',
  'seniority_bonus',
  'sm_calculated',
  'adjustments',
  'total_to_pay',
  'irpf',
  'actions',
];
const HOURLY_COLUMNS = [
  'period',
  'hours',
  'hours_period',
  'price_per_hour',
  'brute_base_salary',
  'adjustments',
  'total_to_pay',
  'irpf',
  'actions',
];

@Component({
  selector: 'app-my-payrolls-list',
  templateUrl: './my-payrolls-list.component.html',
  styleUrls: ['./my-payrolls-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    PercentPipe,
  ],
})
export class MyPayrollsListComponent extends IndexTemplateComponent implements OnInit {
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private currencyPipe = inject(CurrencyPipe);
  private user = inject(UserService);
  private readonly utils_service = inject(UtilsService);

  filterForm: UntypedFormGroup;

  isHourBasedContract = false;

  public override displayedColumns: string[] = [...SALARY_COLUMNS];

  public dataSource: MatTableDataSource<Payroll> = new MatTableDataSource<Payroll>();

  constructor() {
    super(inject(Router), inject(FormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.isHourBasedContract = this.detectHourBasedFromUser();
    if (this.isHourBasedContract) {
      this.displayedColumns = [...HOURLY_COLUMNS];
    }
    super.ngOnInit();
  }

  override fetchData(): void {
    const params = { ...this.filterForm.value };

    this.api.getMyPayrolls(params).subscribe({
      next: data => {
        this.dataSource = new MatTableDataSource<Payroll>(data.data);
        // Solo contratos por horas / prácticas, o nóminas con €/h.
        // Las nóminas de salario también pueden llevar horas contabilizadas: no deben ocultar el resumen.
        this.isHourBasedContract = this.detectHourBasedFromUser() || data.data.some((payroll: Payroll) => payroll.price_per_hour != null);
        this.displayedColumns = this.isHourBasedContract ? [...HOURLY_COLUMNS] : [...SALARY_COLUMNS];
        this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
      },
      error: () => {},
      complete() {},
    });
  }

  private detectHourBasedFromUser(): boolean {
    const contracts = this.user.getCurrentUser()?.working_contracts ?? [];
    return contracts.some(contract => PER_HOUR_CONTRACT_SLUGS.includes(contract.working_contract_type?.slug));
  }

  periodLabel(payroll: Payroll): string {
    return dayjs(payroll.working_period_from).format('MM/YYYY');
  }

  hoursWorked(payroll: Payroll): number | null {
    const hours = payroll.hours_worked_since_last_day_taken_into_account_for_hours;
    return hours == null ? null : Number(hours);
  }

  /**
   * Periodo de horas contabilizado: del día siguiente al último día de la nómina anterior
   * hasta el último día tenido en cuenta en esta nómina.
   */
  hoursAccountedPeriod(payroll: Payroll, index: number): string | null {
    const end = payroll.last_day_taken_into_account_for_hours;
    if (!end) {
      return null;
    }

    const endDay = dayjs.utc(end).startOf('day');
    const previous = this.dataSource.data[index + 1];
    const startDay = previous?.last_day_taken_into_account_for_hours
      ? dayjs.utc(previous.last_day_taken_into_account_for_hours).startOf('day').add(1, 'day')
      : dayjs(payroll.working_period_from).startOf('day');

    return `${startDay.format('DD/MM/YYYY')} – ${endDay.format('DD/MM/YYYY')}`;
  }

  baseSalary(payroll: Payroll): number {
    return payrollLineAmountBySlug(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.BASE_SALARY);
  }

  seniorityBonus(payroll: Payroll): number {
    return payrollLineAmountBySlug(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.SENIORITY_BONUS);
  }

  plusesTotal(payroll: Payroll): number {
    return bisualPlusesTotal(payroll.payroll_lines);
  }

  smCalculated(payroll: Payroll): number {
    if (this.isHourBasedContract) {
      return this.baseSalary(payroll);
    }
    return this.baseSalary(payroll) + this.seniorityBonus(payroll) + this.plusesTotal(payroll);
  }

  fuelCost(payroll: Payroll): number {
    return payrollLineAmountBySlug(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE);
  }

  additionalHoursAmount(payroll: Payroll): number {
    return payrollLineAmountBySlug(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.EXTRA_HOURS);
  }

  flexibleRemunerationAmount(payroll: Payroll): number {
    return payrollLineAmountBySlug(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION);
  }

  totalToPay(payroll: Payroll): number | null {
    const draft = payroll.latest_accounting_draft;
    if (draft?.status !== 'synced') {
      return null;
    }
    const value = draft.total_to_pay;
    if (value == null || Number.isNaN(Number(value))) {
      return null;
    }
    return Number(value);
  }

  additionalSalaryAmount(payroll: Payroll): number {
    return payrollLineAmountBySlug(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.ADDITIONAL_SALARY);
  }

  getAdjustmentChips(payroll: Payroll): PayrollAdjustmentChip[] {
    const chips: PayrollAdjustmentChip[] = [];
    const pluses_total = this.plusesTotal(payroll);
    const justification = payroll.plus_justifications ?? null;
    const bisual_plus_original_amount = justification?.bisual_plus_original_amount;
    const plus_after_kpi = justification?.bisual_plus_amount_after_kpi;
    const plus_kpi_cut =
      bisual_plus_original_amount != null && plus_after_kpi != null && bisual_plus_original_amount > plus_after_kpi
        ? Math.round((bisual_plus_original_amount - plus_after_kpi) * 100) / 100
        : 0;

    if (pluses_total > 0) {
      chips.push({
        key: 'plus',
        emoji: '🎖️',
        label: plus_kpi_cut > 0 ? 'Plus Bisual (KPI aplicado)' : 'Plus Bisual',
        amount: pluses_total,
        text: plus_kpi_cut > 0 ? `KPI −${this.currencyPipe.transform(plus_kpi_cut)}` : undefined,
        kind: PAYROLL_CONCEPT_KIND.SALARY,
        is_expected_normal: true,
        is_kpi_cut: plus_kpi_cut > 0,
      });
    }

    const fuel = this.fuelCost(payroll);
    if (fuel > 0) {
      const km = payrollLineMetaNumber(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE, 'km');
      chips.push({
        key: PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE,
        emoji: '⛽',
        label: km > 0 ? `Gasolina / kilometraje (${km} km)` : 'Gasolina / kilometraje',
        amount: fuel,
        kind: PAYROLL_CONCEPT_KIND.EXPENSE,
      });
    }

    const extra_hours = payrollLineMetaNumber(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.EXTRA_HOURS, 'hours');
    const extra_salary = this.additionalSalaryAmount(payroll);
    const extra_hours_amount = this.additionalHoursAmount(payroll);
    if (extra_hours > 0 || extra_salary > 0 || extra_hours_amount > 0) {
      chips.push({
        key: PAYROLL_CONCEPT_SLUG.EXTRA_HOURS,
        emoji: '⏱️',
        label: 'Horas / importe extra',
        amount: extra_hours_amount > 0 ? extra_hours_amount : extra_salary > 0 ? extra_salary : null,
        text: extra_hours > 0 ? `${extra_hours} h` : undefined,
        kind: PAYROLL_CONCEPT_KIND.SALARY,
      });
    }

    const flex = this.flexibleRemunerationAmount(payroll);
    if (flex > 0) {
      const extracted = payrollLineMetaBoolean(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION, 'extracted_from_salary');
      chips.push({
        key: PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
        emoji: '🍽️',
        label: extracted ? 'Ret. flexible (excluida del SM)' : 'Ret. flexible (incluida)',
        amount: flex,
        kind: PAYROLL_CONCEPT_KIND.SALARY,
      });
    }

    for (const chip of extraPayrollConceptLineChips(payroll.payroll_lines, [
      PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE,
      PAYROLL_CONCEPT_SLUG.EXTRA_HOURS,
      PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
      PAYROLL_CONCEPT_SLUG.ADDITIONAL_SALARY,
    ])) {
      chips.push(chip);
    }

    if (payroll.comment_for_employee?.trim()) {
      chips.push({
        key: 'note',
        emoji: '📝',
        label: 'Nota para el empleado',
        amount: null,
        text: '···',
      });
    }

    return chips;
  }

  formatAdjustmentAmount(amount: number, withSign = true): string {
    const absolute = this.currencyPipe.transform(Math.abs(amount)) ?? `${Math.abs(amount)}`;
    if (!withSign) {
      return absolute;
    }
    if (amount > 0) {
      return `+${absolute}`;
    }
    if (amount < 0) {
      return `−${absolute}`;
    }
    return absolute;
  }

  smCalculatedTooltip(payroll: Payroll): string {
    const currency = (value: number): string => this.currencyPipe.transform(value) ?? `${value}`;
    if (this.isHourBasedContract) {
      const hours = this.hoursWorked(payroll);
      const price = Number(payroll.price_per_hour || 0);
      if (hours != null && price > 0) {
        return `${hours} h × ${currency(price)}/h`;
      }
      return `Importe: ${currency(this.baseSalary(payroll))}`;
    }

    const lines = [`Base: ${currency(this.baseSalary(payroll))}`, `Seniority: ${currency(this.seniorityBonus(payroll))}`];
    const justification = payroll.plus_justifications as PayrollPlusJustification | null;
    if (justification?.bisual_plus_original_amount != null && justification?.bisual_plus_amount_after_kpi != null) {
      lines.push(
        `Plus Bisual: ${currency(justification.bisual_plus_original_amount)} → ${currency(justification.bisual_plus_amount_after_kpi)}` +
          (justification.kpi_average_factor != null ? ` (${Math.round(justification.kpi_average_factor * 100)}%)` : '')
      );
    } else {
      for (const line of bisualPlusPayrollLines(payroll.payroll_lines)) {
        lines.push(`${line.lineable?.name ?? 'Plus'}: ${currency(Number(line.amount ?? 0))}`);
      }
    }
    return lines.join('\n');
  }

  downloadPdf(payroll: Payroll): void {
    this.api.downloadPayrollPdf(payroll.id.toString()).subscribe(data => {
      this.utils_service.downloadPdfFromBlobResponse(data);
    });
  }

  showPayrollJustification(payroll: Payroll): void {
    const period_label = this.periodLabel(payroll);
    const pluses_total = this.plusesTotal(payroll);
    const distance_in_km = payrollLineMetaNumber(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE, 'km');
    const fuel_cost_per_km = payrollLineMetaNumber(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE, 'rate');
    const brute_base_salary = this.baseSalary(payroll);
    const seniority_bonus = this.seniorityBonus(payroll);
    const additional_hours = payrollLineMetaNumber(payroll.payroll_lines, PAYROLL_CONCEPT_SLUG.EXTRA_HOURS, 'hours');

    this.dialog.open<PayrollPlusesJustificationDialogComponent, PayrollPlusesJustificationDialogData>(
      PayrollPlusesJustificationDialogComponent,
      {
        width: '680px',
        maxWidth: '95vw',
        data: {
          title: 'Justificación de mi nómina',
          period_label,
          justification: payroll.plus_justifications ?? null,
          breakdown: {
            brute_base_salary,
            seniority_bonus,
            pluses_total,
            flexible_remuneration: this.flexibleRemunerationAmount(payroll),
            extract_flexible_remuneration_from_salary: payrollLineMetaBoolean(
              payroll.payroll_lines,
              PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
              'extracted_from_salary'
            ),
            distance_in_km,
            fuel_cost_per_km,
            total_fuel_cost: this.fuelCost(payroll),
            additional_salary: this.additionalSalaryAmount(payroll),
            additional_hours,
            additional_hours_amount: this.additionalHoursAmount(payroll),
            sm_calculated: this.smCalculated(payroll),
            comments: null,
            comment_for_employee: payroll.comment_for_employee ?? null,
          },
          payroll_id: payroll.id,
          can_view_mileage_detail: true,
        },
      }
    );
  }
}
